interface RequestMetrics {
  count: number;
  lastCalled: number;
  averageResponseTime: number;
  totalResponseTime: number;
  errors: number;
  duplicates: number;
}

interface RequestLog {
  timestamp: number;
  method: string;
  params?: any;
  responseTime?: number;
  success: boolean;
  error?: string;
  isDuplicate: boolean;
}

export class RequestMonitor {
  private static instance: RequestMonitor;
  private metrics = new Map<string, RequestMetrics>();
  private logs: RequestLog[] = [];
  private maxLogs = 1000; // Keep only last 1000 logs

  static getInstance(): RequestMonitor {
    if (!RequestMonitor.instance) {
      RequestMonitor.instance = new RequestMonitor();
    }
    return RequestMonitor.instance;
  }

  private constructor() {
    // Private constructor for singleton
  }

  startRequest(method: string, params?: any): { requestId: string; isDuplicate: boolean } {
    const requestId = `${method}_${Date.now()}_${Math.random()}`;
    const key = this.getKey(method, params);
    
    // Update metrics
    const metrics = this.metrics.get(key) || {
      count: 0,
      lastCalled: 0,
      averageResponseTime: 0,
      totalResponseTime: 0,
      errors: 0,
      duplicates: 0,
    };

    // Check for duplicates (requests within 100ms of each other)
    const isDuplicate = Date.now() - metrics.lastCalled < 100;
    
    if (isDuplicate) {
      metrics.duplicates++;
      if (process.env.NODE_ENV === 'development') {
        console.warn(`🚨 Duplicate request detected: ${method}`, {
          timeSinceLastCall: Date.now() - metrics.lastCalled,
          totalDuplicates: metrics.duplicates,
          params
        });
      }
    }

    metrics.count++;
    metrics.lastCalled = Date.now();
    this.metrics.set(key, metrics);

    // Add to logs
    this.addLog({
      timestamp: Date.now(),
      method,
      params,
      success: false, // Will be updated on completion
      isDuplicate,
    });

    return { requestId, isDuplicate };
  }

  endRequest(
    method: string, 
    params: any, 
    success: boolean, 
    responseTime: number, 
    error?: string
  ): void {
    const key = this.getKey(method, params);
    const metrics = this.metrics.get(key);
    
    if (metrics) {
      if (!success) {
        metrics.errors++;
      }
      
      metrics.totalResponseTime += responseTime;
      metrics.averageResponseTime = metrics.totalResponseTime / metrics.count;
      
      this.metrics.set(key, metrics);
    }

    // Update the most recent log entry
    const recentLog = this.logs
      .filter(log => log.method === method && !log.hasOwnProperty('responseTime'))
      .pop();
    
    if (recentLog) {
      recentLog.success = success;
      recentLog.responseTime = responseTime;
      recentLog.error = error;
    }
  }

  private addLog(log: RequestLog): void {
    this.logs.push(log);
    
    // Keep only the most recent logs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }
  }

  private getKey(method: string, params?: any): string {
    if (params && typeof params === 'object') {
      return `${method}_${JSON.stringify(params)}`;
    }
    return method;
  }

  getMetrics(): Map<string, RequestMetrics> {
    return new Map(this.metrics);
  }

  getLogs(method?: string): RequestLog[] {
    if (method) {
      return this.logs.filter(log => log.method === method);
    }
    return [...this.logs];
  }

  getDuplicateReport(): { method: string; duplicates: number; totalCalls: number }[] {
    const report: { method: string; duplicates: number; totalCalls: number }[] = [];
    
    this.metrics.forEach((metrics, key) => {
      if (metrics.duplicates > 0) {
        report.push({
          method: key,
          duplicates: metrics.duplicates,
          totalCalls: metrics.count,
        });
      }
    });

    return report.sort((a, b) => b.duplicates - a.duplicates);
  }

  getPerformanceReport(): { method: string; averageTime: number; errorRate: number; callCount: number }[] {
    const report: { method: string; averageTime: number; errorRate: number; callCount: number }[] = [];
    
    this.metrics.forEach((metrics, key) => {
      report.push({
        method: key,
        averageTime: Math.round(metrics.averageResponseTime),
        errorRate: metrics.count > 0 ? (metrics.errors / metrics.count) * 100 : 0,
        callCount: metrics.count,
      });
    });

    return report.sort((a, b) => b.callCount - a.callCount);
  }

  reset(): void {
    this.metrics.clear();
    this.logs = [];
  }

  // Development helper methods
  logSummary(): void {
    if (process.env.NODE_ENV === 'development') {
      console.group('📊 Request Monitor Summary');
    }
    
    const duplicateReport = this.getDuplicateReport();
    if (process.env.NODE_ENV === 'development' && duplicateReport.length > 0) {
      console.warn('🚨 Duplicate Requests Detected:');
      duplicateReport.forEach(item => {
        console.warn(`  ${item.method}: ${item.duplicates}/${item.totalCalls} calls were duplicates`);
      });
    }

    const performanceReport = this.getPerformanceReport();
    if (process.env.NODE_ENV === 'development') {
      console.log('⚡ Performance Report:');
    }
    performanceReport.slice(0, 10).forEach(item => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`  ${item.method}: ${item.callCount} calls, ${item.averageTime}ms avg, ${item.errorRate.toFixed(1)}% errors`);
      }
    });

    if (process.env.NODE_ENV === 'development') {
      console.groupEnd();
    }
  }

  // Auto-log summary every 30 seconds in development
  startAutoLogging(): void {
    if (process.env.NODE_ENV === 'development') {
      setInterval(() => {
        if (this.metrics.size > 0) {
          this.logSummary();
        }
      }, 30000);
    }
  }
}

// Export singleton instance
export const requestMonitor = RequestMonitor.getInstance(); 